import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { Bell } from "lucide-react";
import api from "../../lib/api";
import { PeriodPicker } from "./PeriodPicker";

const eur = (v) => `${Number(v).toFixed(2)} €`;

const StatTable = ({ title, rows, nameKey = "name", testId }) => (
  <div data-testid={testId} className="bg-white border border-black/5 p-6">
    <p className="label-editorial text-black/50 mb-4">{title}</p>
    <table className="w-full text-sm font-light">
      <thead>
        <tr className="text-left text-xs uppercase tracking-widest text-black/40 border-b border-black/5">
          <th className="py-2 pr-2"></th>
          <th className="py-2 pr-2 text-right">Total</th>
          <th className="py-2 pr-2 text-right">Nº</th>
          <th className="py-2 text-right">Média</th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && (
          <tr><td colSpan={4} className="py-4 text-black/30">Sem dados neste mês.</td></tr>
        )}
        {rows.map((r) => (
          <tr key={r[nameKey]} className="border-b border-black/5 last:border-0">
            <td className="py-2.5 pr-2">{r[nameKey]}</td>
            <td className="py-2.5 pr-2 text-right font-medium">{eur(r.total)}</td>
            <td className="py-2.5 pr-2 text-right text-black/50">{r.count}</td>
            <td className="py-2.5 text-right text-black/50">{eur(r.avg)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const Dashboard = ({ newBookings = 0, onOpenBookings }) => {
  const [range, setRange] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!range) return;
    api.get("/admin/stats", { params: { start: range.start, end: range.end } }).then((res) => setStats(res.data));
  }, [range]);

  return (
    <div className="space-y-8">
      {newBookings > 0 && (
        <button
          data-testid="dashboard-new-bookings"
          onClick={onOpenBookings}
          className="w-full flex items-center justify-between gap-4 bg-clay text-white p-6 hover:bg-clay-dark transition-colors duration-300 text-left"
        >
          <div className="flex items-center gap-4">
            <Bell size={22} className="shrink-0" />
            <div>
              <p className="label-editorial text-white/70">Marcações novas</p>
              <p className="font-display text-2xl md:text-3xl mt-1 tracking-tight">
                {newBookings} nova{newBookings > 1 ? "s" : ""} marcação{newBookings > 1 ? "ões" : ""}
              </p>
            </div>
          </div>
          <span className="label-editorial whitespace-nowrap">Ver marcações →</span>
        </button>
      )}
      <PeriodPicker onRange={setRange} defaultMode="mes" testPrefix="dashboard-period" />
      {!stats ? (
        <p className="label-editorial text-black/40">A carregar…</p>
      ) : (
        <DashboardBody stats={stats} />
      )}
    </div>
  );
};

const DashboardBody = ({ stats }) => {
  const chartData = stats.by_day.map((d) => ({ ...d, day: d.date.slice(8) }));

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-black/5 border border-black/5">
        {[
          { label: "Vendas", value: eur(stats.totals.total), id: "stat-total" },
          { label: "Nº Vendas", value: stats.totals.count, id: "stat-count" },
          { label: "Venda Média", value: eur(stats.totals.avg), id: "stat-avg" },
        ].map((c) => (
          <div key={c.id} data-testid={c.id} className="bg-white p-8">
            <p className="label-editorial text-black/50">{c.label}</p>
            <p className="font-display text-4xl md:text-5xl mt-3 tracking-tight">{c.value}</p>
          </div>
        ))}
      </div>

      {stats.bookings && (
        <div data-testid="stats-bookings-source" className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-black/5 border border-black/5">
          {[
            { label: "Marcações", value: stats.bookings.total, id: "stat-bk-total" },
            { label: "Do Site", value: stats.bookings.site, id: "stat-bk-site" },
            { label: "Internas", value: stats.bookings.interna, id: "stat-bk-interna" },
          ].map((c) => (
            <div key={c.id} data-testid={c.id} className="bg-white p-8">
              <p className="label-editorial text-black/50">{c.label}</p>
              <p className="font-display text-4xl md:text-5xl mt-3 tracking-tight">{c.value}</p>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white border border-black/5 p-6">
        <p className="label-editorial text-black/50 mb-6">Total de vendas por dia</p>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <XAxis dataKey="day" tick={{ fontSize: 11 }} stroke="#999" />
              <YAxis tick={{ fontSize: 11 }} stroke="#999" />
              <Tooltip formatter={(v) => eur(v)} labelFormatter={(l) => `Dia ${l}`} />
              <Bar dataKey="total" fill="#111111" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <StatTable title="Vendas por Team Member" rows={stats.by_staff} testId="stats-by-staff" />
        <StatTable title="Vendas por Serviço" rows={stats.by_service.slice(0, 8)} testId="stats-by-service" />
        <StatTable title="Vendas por Pagamento" rows={stats.by_payment} testId="stats-by-payment" />
      </div>
    </div>
  );
};
